/*
 * BEWARE: getting these curved flight paths right is harder than it seems! Please manually test
 * the following cases:
 * exactly vertical route (manually set lng the same for start and end)
 * exactly horizontal route (manually set lat the same for start and end)
 * near-horizontal route (manually set lng to ~0.4 apart)
 * near-vertical route (manually set lat to ~0.4 apart)
 */

import type { ComponentProps } from 'react'
import * as React from 'react'
import styled from 'styled-components'

import type { FlightDirection } from './flightPathUtils'
import {
  xyPerpendicularToMidpoint,
  quadBezierAtT,
  quadBezierAngle,
  directionalStartEnd,
} from './flightPathUtils'
import SvgIcon from 'LuxKit/SvgIcon'
import { rem } from 'polished'

const Container = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 1px;
  min-width: 1px;

  svg.curved-line {
    position: absolute;
  }
`

const PinPositioner = styled.div<{ top: number, left: number }>`
  position: absolute;
  top: ${({ top }) => top}%;
  left: ${({ left }) => left}%;
  transform: translate(-50%, -50%);
`

const ClickableStroke = styled(SvgIcon)`
  pointer-events: none;
  overflow: visible;
  width: 100%;
  height: 100%;

  & > path {
    pointer-events: stroke;
    cursor: pointer;
  }

  .background {
    stroke: ${({ theme }) => theme.palette.neutral.default.eight};
    stroke-width: 4;
  }

  .foreground {
    stroke: ${({ theme }) => theme.palette.neutral.default.one};
    stroke-width: 2;
  }
`

const Chevron = styled.div`
  position: absolute;
  width: ${rem(16)};
  height: ${rem(16)};
  transform-origin: center;

  > svg {
    width: 100%;
    height: 100%;
    stroke-width: 2;
    stroke: currentColor;
    pointer-events: none;
  }

  svg path {
    pointer-events: stroke;
    cursor: pointer;
  }

  .background {
    stroke: ${({ theme }) => theme.palette.neutral.default.eight};
    stroke-width: 4;
  }

  .foreground {
    stroke: ${({ theme }) => theme.palette.neutral.default.one};
    stroke-width: 2;
  }
`

const AirportDot = styled.div`
  border-radius: ${(props) => props.theme.borderRadius.round};
  background: black;
  border: 1px solid white;
  width: ${rem(8)};
  height: ${rem(8)};
  position: absolute;
  pointer-events: initial;
  cursor: pointer;

  &.top {
    top: -4px;
  }

  &.bottom {
    bottom: -4px;
  }

  &.left {
    left: -4px;
  }

  &.right {
    right: -4px;
  }
`

const MAX_LINE_SIZE = 1000000

const commonProps: Partial<React.SVGProps<SVGPathElement>> = {
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  vectorEffect: 'non-scaling-stroke',
  fill: 'none',
}

interface Props
  extends Omit<ComponentProps<typeof ClickableStroke>, 'viewBox'> {
  active: boolean
  marker: React.ReactNode
  direction: FlightDirection
  pxWidth: number
  pxHeight: number
  onClick: () => void
}

function MapPath({
  orientation,
  marker,
  onClick,
  direction,
  pxWidth: width,
  pxHeight: height,
  active,
  ...rest
}: Props) {
  let CURVE_FACTOR = 0.2
  if (direction === 'SE' || direction === 'SW') {
    // So return flight paths don't overlap, make all south-bound flights more curved
    CURVE_FACTOR = 0.3
  }
  const straightLineDist = Math.sqrt(Math.pow(width, 2) + Math.pow(height, 2))
  let dist = straightLineDist * CURVE_FACTOR

  const MIN_SIZE = 5
  if (width < MIN_SIZE || height < MIN_SIZE) {
    // just render a straight line, we have too many problems with
    // near-vertical or near-horizontal paths.
    // See more notes in the PR: https://github.com/lux-group/www-le-customer/pull/10343
    dist = 0
  }

  const { x: curveX, y: curveY } = xyPerpendicularToMidpoint(
    width,
    height,
    direction,
    dist,
  )

  const [[x0, y0], [x4, y4]] = directionalStartEnd(width, height, direction)

  const p0 = { x: x0, y: y0 }
  const p1 = { x: curveX, y: curveY }
  const p2 = { x: x4, y: y4 }

  const { x: x1, y: y1 } = quadBezierAtT(p0, p1, p2, 0.25)
  const { x: x2, y: y2 } = quadBezierAtT(p0, p1, p2, 0.5)
  const { x: x3, y: y3 } = quadBezierAtT(p0, p1, p2, 0.75)

  const q1Radians = quadBezierAngle(p0, p1, p2, 0.25)
  const q3Radians = quadBezierAngle(p0, p1, p2, 0.75)

  const path = `M${x0},${y0} Q${curveX},${curveY} ${x4},${y4}`

  // viewBox will show nothing if it's not at least 1 unit wide/hight
  const viewBox = `0 0 ${width || 1} ${height || 1}`

  const showLine = width < MAX_LINE_SIZE && height < MAX_LINE_SIZE

  const chevronSvg = (
    <svg
      width="8"
      height="14"
      viewBox="0 0 8 14"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M1.5 12L6.5 7L1.5 2"
        className="background"
        strokeLinecap="square"
        onClick={onClick}
      />
      <path
        d="M1.5 12L6.5 7L1.5 2"
        className="foreground"
        strokeLinecap="square"
        onClick={onClick}
      />
    </svg>
  )

  return (
    <Container>
      {showLine && (
        <ClickableStroke
          {...rest}
          colour={active ? 'primary' : 'neutral-one'}
          viewBox={viewBox}
          calcMode="spline"
          preserveAspectRatio="none"
          className="curved-line"
        >
          <path
            {...commonProps}
            d={path}
            className="background"
            onClick={onClick}
          />
          <path
            {...commonProps}
            d={path}
            className="foreground"
            onClick={onClick}
          />
        </ClickableStroke>
      )}
      {(direction == 'NE' || direction == 'SW') && (
        <>
          <AirportDot className="top right" onClick={onClick} />
          <AirportDot className="bottom left" onClick={onClick} />
        </>
      )}
      {(direction == 'NW' || direction == 'SE') && (
        <>
          <AirportDot className="top left" onClick={onClick} />
          <AirportDot className="bottom right" onClick={onClick} />
        </>
      )}
      {showLine && straightLineDist > 120 && (
        <>
          <Chevron
            style={{
              left: `${(x1 / width) * 100}%`,
              top: `${(y1 / height) * 100}%`,
              transform: `translate(-50%, -50%) rotate(${q1Radians}rad)`,
            }}
          >
            {chevronSvg}
          </Chevron>
          <Chevron
            style={{
              left: `${(x3 / width) * 100}%`,
              top: `${(y3 / height) * 100}%`,
              transform: `translate(-50%, -50%) rotate(${q3Radians}rad)`,
            }}
          >
            {chevronSvg}
          </Chevron>
        </>
      )}
      {showLine && straightLineDist > 40 && (
        <PinPositioner left={(x2 / width) * 100} top={(y2 / height) * 100}>
          {marker}
        </PinPositioner>
      )}
    </Container>
  )
}

export default MapPath
